/**
 * bookTree.js — pure, stateless regroup logic for the position-book cascading tree
 * (PORTAL → MEMBER → FUND/position cards) on LedgerDashboardPage.
 *
 * The wire contract is UNCHANGED — the position-book bridge (backend) still emits the
 * classic overview shape: portals[].funds[].members[].positions[]. This module derives
 * a DIFFERENT client-side view of the same data: instead of grouping by fund first (the
 * classic flat fund list), it groups by portal → investor (member) → funds[], "exactly
 * like the data is built" (founder ask, see vault/90-Active/PLAN-position-book-ledger-tree.md).
 *
 * buildBookTree(portals) -> PortalNode[]
 *
 * Each input portal: { portal, total_nav, funds: Fund[] }
 * Each input fund:    { fund_name, fund_display_name, currency, kit, members: Member[],
 *                        total_commitment, total_distributed, total_nav, ... }
 * Each input member:  { investor_name, commitment_total, capital_called_cumulative,
 *                        capital_contributed_cumulative, distributions_cumulative, nav,
 *                        tvpi, dpi, irr, irr_reliable, currency, stream_id, positions:[], ... }
 *
 * PortalNode: { portal, total_nav, nav_by_ccy, member_count, members: MemberNode[] }
 * MemberNode: { investor_name, portal, funds: Fund[], rollup: Rollup }
 *   - funds: the ORIGINAL fund object for every fund this investor holds a position in,
 *     each cloned shallowly with members replaced by [thatInvestor'sOwnMemberObject] — the
 *     member object itself (incl. its .positions[]) is passed through UNTOUCHED so <FundRow>
 *     (and everything it nests — MemberRow/PositionSubRow/MemberTimeline/ValueSparkline)
 *     renders it with zero changes to its own logic.
 * Rollup: { commitment_total, capital_called_cumulative, distributions_cumulative, nav,
 *           nav_by_ccy, tvpi, dpi, irr: null, irr_reliable: false, currency, fund_count }
 *
 * Rollup math is null-aware: summing a field where every contributing fund reported null
 * yields null (never 0 — absence of data is not "zero"); summing a field with a MIX of
 * numbers and nulls sums only the non-null values. irr is NEVER blended across funds — a
 * member's rollup irr is always null/unreliable; the page must not synthesize a return %
 * that mixes unrelated cashflow streams. currency is the single shared currency across the
 * member's funds when they agree, else the literal string "mixed" (unknown/absent
 * currencies are ignored when computing the shared set, not merged in as their own value).
 *
 * nav_by_ccy is a currency -> null-aware-summed-nav map, one bucket per effective currency
 * among the member's funds (a fund with a nav but no resolvable currency buckets under
 * "USD" to match the backend default). When currency is "mixed" the scalar nav/tvpi/dpi are
 * forced to null — blending NAV across currencies (e.g. adding ILS to USD) is dishonest, so
 * the page must render per-currency chips from nav_by_ccy instead of a blended number.
 *
 * Portals are returned sorted by total_nav descending (nulls last) — the order the page's
 * PortalCard list renders in. When total_nav is null (a mixed-currency portal), the sort
 * falls back to the sum of the portal's nav_by_ccy values as a display-only magnitude —
 * that fallback number is for ORDERING ONLY and must never be rendered as a NAV figure.
 * Members are NOT re-sorted here (kept in first-seen order — the order their funds appear
 * in the already-filtered wire tree); the page scopes ledgerLeaderboard.sortFunds to each
 * member's own funds[] list separately for "Sort by".
 */

// Null-aware sum: every value null -> null; a mix of numbers and nulls -> sum of the
// non-null values (nulls are treated as "unknown", never coerced to 0).
function nullAwareSum(values) {
  let sum = null;
  for (const v of values) {
    if (v == null) continue;
    const n = Number(v);
    if (Number.isNaN(n)) continue;
    sum = (sum ?? 0) + n;
  }
  return sum;
}

// A leaf's effective currency: the member's own currency if set, else the fund's.
function effectiveCurrency(member, fund) {
  const raw = member.currency || fund.currency;
  if (!raw) return null;
  const code = String(raw).trim().toUpperCase();
  return code || null;
}

/**
 * computeMemberRollup(leaves) -> Rollup
 * leaves: [{ member, fund }] — every (fund, member-of-that-investor) pair for one investor
 * within one portal.
 */
export function computeMemberRollup(leaves) {
  const members = leaves.map((l) => l.member);

  const commitment_total = nullAwareSum(members.map((m) => m.commitment_total));
  const capital_called_cumulative = nullAwareSum(
    members.map((m) => (m.capital_called_cumulative ?? m.capital_contributed_cumulative))
  );
  const distributions_cumulative = nullAwareSum(members.map((m) => m.distributions_cumulative));
  const navRaw = nullAwareSum(members.map((m) => m.nav));

  const currencySet = new Set();
  for (const { member, fund } of leaves) {
    const ccy = effectiveCurrency(member, fund);
    if (ccy) currencySet.add(ccy);
  }
  const currency = currencySet.size === 0 ? null : currencySet.size === 1 ? [...currencySet][0] : "mixed";
  const isMixed = currency === "mixed";

  // nav_by_ccy: one bucket per effective currency among this member's funds (null-aware sum
  // within each bucket); a fund with a nav but no resolvable currency buckets under "USD"
  // to match the backend default.
  const navByCcyGroups = new Map();
  for (const { member, fund } of leaves) {
    const ccy = effectiveCurrency(member, fund) || "USD";
    if (!navByCcyGroups.has(ccy)) navByCcyGroups.set(ccy, []);
    navByCcyGroups.get(ccy).push(member.nav);
  }
  const nav_by_ccy = {};
  for (const [ccy, navs] of navByCcyGroups) {
    nav_by_ccy[ccy] = nullAwareSum(navs);
  }

  // Never present a blended cross-currency total: when the member's funds disagree on
  // currency, the scalar nav/tvpi/dpi are null — the page renders per-currency chips from
  // nav_by_ccy instead. Single-currency (or fully-unknown-currency) members are unchanged.
  const nav = isMixed ? null : navRaw;

  const invested = capital_called_cumulative;
  const hasInvested = !isMixed && invested != null && invested > 0;
  const tvpi = hasInvested ? ((distributions_cumulative ?? 0) + (nav ?? 0)) / invested : null;
  const dpi = hasInvested && distributions_cumulative != null ? distributions_cumulative / invested : null;

  return {
    commitment_total,
    capital_called_cumulative,
    distributions_cumulative,
    nav,
    nav_by_ccy,
    tvpi,
    dpi,
    // NEVER blend irr across funds — a rollup irr would mix unrelated cashflow streams.
    irr: null,
    irr_reliable: false,
    currency,
    fund_count: leaves.length,
  };
}

/**
 * buildBookTree(portals) -> PortalNode[]
 * Pure — never mutates the input tree. Intended input is the ALREADY-FILTERED portals
 * array (applyLedgerFilters runs first; this only regroups whatever survived).
 */
export function buildBookTree(portals) {
  const portalNodes = [];

  for (const portal of portals || []) {
    // investor_name -> { investor_name, funds: [], _leaves: [] }
    const memberOrder = [];
    const memberByName = new Map();

    for (const fund of portal.funds || []) {
      for (const member of fund.members || []) {
        const key = String(member.investor_name || "");
        let node = memberByName.get(key);
        if (!node) {
          node = { investor_name: member.investor_name, funds: [], _leaves: [] };
          memberByName.set(key, node);
          memberOrder.push(key);
        }
        // Original fund object shape, members replaced with JUST this investor's own
        // member node (untouched reference — positions[] passthrough included).
        node.funds.push({ ...fund, members: [member] });
        node._leaves.push({ member, fund });
      }
    }

    const members = memberOrder.map((key) => {
      const node = memberByName.get(key);
      return {
        investor_name: node.investor_name,
        portal: portal.portal,
        funds: node.funds,
        rollup: computeMemberRollup(node._leaves),
      };
    });

    // Portal-level nav_by_ccy: element-wise null-aware sum of every member's nav_by_ccy map.
    const nav_by_ccy = {};
    for (const m of members) {
      for (const [ccy, val] of Object.entries(m.rollup.nav_by_ccy || {})) {
        if (!(ccy in nav_by_ccy)) nav_by_ccy[ccy] = null;
        if (val != null) nav_by_ccy[ccy] = (nav_by_ccy[ccy] ?? 0) + Number(val);
      }
    }

    // total_nav is a single scalar ONLY when every member of the portal shares one
    // currency (a member that is itself "mixed", or two members on different single
    // currencies, both make the portal-level total unblendable) — else null, same "never
    // present a blended cross-currency total" rule as the member rollup.
    let hasMixedMember = false;
    const memberCurrencies = new Set();
    for (const m of members) {
      const c = m.rollup.currency;
      if (c === "mixed") hasMixedMember = true;
      else if (c) memberCurrencies.add(c);
    }
    const portalSingleCurrency = !hasMixedMember && memberCurrencies.size <= 1;
    const total_nav = portalSingleCurrency ? nullAwareSum(members.map((m) => m.rollup.nav)) : null;

    portalNodes.push({
      portal: portal.portal,
      total_nav,
      nav_by_ccy,
      member_count: members.length,
      members,
    });
  }

  // Sort portals by total_nav descending (nulls last). When total_nav is null (a
  // mixed-currency portal), fall back to the sum of nav_by_ccy as a display-only ordering
  // magnitude — this fallback is for SORTING ONLY and must never be rendered as a NAV number.
  const sortMagnitude = (p) => {
    if (p.total_nav != null) return p.total_nav;
    const vals = Object.values(p.nav_by_ccy || {}).filter((v) => v != null);
    if (vals.length === 0) return null;
    return vals.reduce((sum, v) => sum + Number(v), 0);
  };
  portalNodes.sort((a, b) => {
    const am = sortMagnitude(a);
    const bm = sortMagnitude(b);
    if (am == null && bm == null) return 0;
    if (am == null) return 1;
    if (bm == null) return -1;
    return bm - am;
  });

  return portalNodes;
}
